import { __awaiter } from 'tslib';
import 'long';
import { im_proto } from '../proto/index.js';
import { ErrorType, FlightStatus } from '../core/consts.js';
import { Logger } from '../core/metrics/logger.js';
import { BaseError } from '../core/errors/BaseError.js';
import { Monitor } from '../core/metrics/monitor.js';
import '../core/network/NetworkItem.js';
import { ServiceIdentity } from '../core/service/RuntimeContext.js';
import 'json-long';
import moize from 'moize';
import '../core/model/Message.js';
import 'js-md5';
import '../core/utils/lz4.js';
import '../core/utils/serializer.js';
import { AdapterManager } from '../adapter/manager/AdapterManager.js';
import { AdapterType } from '../adapter/manager/types.js';
import '../core/utils/ticker.js';
import '../adapter/abstract/IMNetApi.js';
import '../core/options.js';
import '../core/utils/uuid.js';
import '../core/service/ConversationManager.js';
import '../core/service/MessageManager.js';
import '@byted/uc-secure-sdk';
import '../core/plugin/DbProxy.js';
import { TrackerItem } from '../core/metrics/trackerTypes.js';
import { BasePlugin } from '../core/plugin/BasePlugin.js';
import { TtlCache } from '../core/utils/ttlCache.js';
import '../core/model/CommandMessage.js';
import { MultimediaApiManager } from './api/MultimediaApiManager.js';
import { FileExtKey, FileType, ImageTplvType, ImageTplvFormat, FileExtTypeValue, MultimediaPluginConsts } from './interfaces.js';
import { getDecryptor, executeDecryptor } from './cipher/index.js';
import PQueue from 'p-queue';

class MultimediaPluginCore extends BasePlugin {
    constructor(ctx) {
        super(ctx);
        this.initAdapter();
    }
    /**
     * 根据消息刷新文件 url
     * @param params.message 消息内容
     * @param params.ignoreCache 是否忽略缓存
     */
    refreshFileUrl(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const msgContent = this.parseContent({
                message: params.message
            });
            const type = msgContent.ext[FileExtKey.Type];
            if (!type) {
                /* istanbul ignore next */
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'this is not a file message',
                    type: ErrorType.MPNotFileMsg,
                    sender: this
                });
            }
            if (this.refreshCache.has(params.message.clientId) && !params.ignoreCache) {
                Logger.ctxDebug(this.ctx, `refresh hit cache: ${params.message.clientId}`);
                return params.message;
            }
            yield this.__internal_getFileUrl(msgContent);
            params.message.content = JSON.stringify({ __files: { [msgContent.displayType]: msgContent } });
            this.resolve(ServiceIdentity.MessageManager).upsert(params.message);
            this.refreshCache.set(params.message.clientId, true);
            return params.message;
        });
    }
    /**
     * 创建资源消息
     * @param params.conversation 目标会话
     * @param params.ext 消息扩展字段 ext
     * @param params.mentionedUsers 需要被 @ 的用户
     * @param params.fileInfo 资源内容
     * @param params.insert 消息创建后是否process处理(新消息处理流程)
     * @param params.referenceMessage 引用消息
     * @param params.referenceHint 透传给业务
     * @param params.type 消息类型
     * @returns 被发送的消息
     */
    createFileMessage(params) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            if (!params.type) {
                switch (params.fileInfo.type) {
                    case FileType.Video:
                        params.type = im_proto.MessageType.MESSAGE_TYPE_VIDEO;
                        break;
                    case FileType.Image:
                        params.type = im_proto.MessageType.MESSAGE_TYPE_IMAGE;
                        break;
                    case FileType.Audio:
                        params.type = im_proto.MessageType.MESSAGE_TYPE_AUDIO;
                        break;
                    default:
                        params.type = im_proto.MessageType.MESSAGE_TYPE_FILE;
                }
            }
            const message = yield this.instance.createMessage({
                conversation: params.conversation,
                content: '',
                ext: params.ext,
                mentionedUsers: params.mentionedUsers,
                type: params.type,
                insert: params.insert,
                clientId: params.clientId,
                referenceMessage: params.referenceMessage,
                referenceHint: params.referenceHint
            });
            message.sendFunc = this.__internal_sendFileMessage.bind(this);
            if (!params.fileInfo.fileHandler && !params.fileInfo.filePath) {
                /* istanbul ignore next */
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'send file with empty file handler or path',
                    type: ErrorType.MPInvalidArgument,
                    sender: this
                });
            }
            params.fileInfo.fileName = (_a = params.fileInfo.fileName) !== null && _a !== void 0 ? _a : (_b = params.fileInfo.fileHandler) === null || _b === void 0 ? void 0 : _b.name;
            params.fileInfo.length = (_c = params.fileInfo.length) !== null && _c !== void 0 ? _c : (_d = params.fileInfo.fileHandler) === null || _d === void 0 ? void 0 : _d.size;
            params.fileInfo.mime = (_e = params.fileInfo.mime) !== null && _e !== void 0 ? _e : (_f = params.fileInfo.fileHandler) === null || _f === void 0 ? void 0 : _f.type;
            message.fileInfo = params.fileInfo;
            return message;
        });
    }
    /**
     * 解析文件类型的消息体内容
     * @param params.message 消息
     */
    parseContent(params) {
        const jsonCon = JSON.parse(params.message.content);
        const files = jsonCon.__files;
        if (!files) {
            /* istanbul ignore next */
            throw new BaseError({
                ctx: this.ctx,
                msg: 'this is not a file message',
                type: ErrorType.MPNotFileMsg,
                sender: this
            });
        }
        const keys = Object.keys(files);
        if (keys.length < 1) {
            /* istanbul ignore next */
            throw new BaseError({
                ctx: this.ctx,
                msg: 'this is not a file message',
                type: ErrorType.MPNotFileMsg,
                sender: this
            });
        }
        return files[keys[0]];
    }
    /**
     * 解密消息体内部资源
     * @param params.message 消息
     * @param params.fetchIndex 代表需要取第x号的资源文件，对于图片消息，第0号是图片原图，第1号是Preview，第2号是Thumb，通常不需要修改
     * @param params.decrpytor 没解密成功时, fallback到提供的decryptor
     */
    decryptMedia(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = this.parseContent({
                message: params.message
            });
            const needDecrpyt = payload.ext[FileExtKey.NeedEncrypt] === '1';
            if (!params.fetchIndex) {
                params.fetchIndex = 0;
            }
            let targetUrl = '';
            let targetUrlRaw = '';
            switch (params.fetchIndex) {
                case 0:
                    targetUrl = payload.encryptUrl;
                    targetUrlRaw = payload.remoteURL;
                    break;
                case 1:
                    targetUrl = payload.ext[FileExtKey.ImgPreviewEncryptUrl];
                    targetUrlRaw = payload.ext[FileExtKey.ImgPreviewUrl];
                    break;
                case 2:
                    targetUrl = payload.ext[FileExtKey.ImgThumbEncryptUrl];
                    targetUrlRaw = payload.ext[FileExtKey.ImgThumbUrl];
                    break;
            }
            if (needDecrpyt && targetUrl) {
                const rawData = yield this.resolve(ServiceIdentity.NetworkManager).sendByHttpTo(targetUrl, 'GET');
                const algorithm = payload.algorithm;
                const secretKey = payload.secretKey;
                const decryptor = getDecryptor(this.ctx, algorithm);
                Logger.ctxDebug(this.ctx, `decrypt msg with algorithm: ${algorithm}, key:${secretKey}`);
                // 使用内置的decryptor先解一次
                let decryptedData = yield executeDecryptor(this.ctx, decryptor, rawData, secretKey);
                // 没解出来, fallback到提供的decryptor
                if (!decryptedData && params.decrpytor) {
                    decryptedData = yield executeDecryptor(this.ctx, params.decrpytor, rawData, secretKey);
                }
                if (!decryptedData) {
                    /* istanbul ignore next */
                    throw new BaseError({
                        ctx: this.ctx,
                        msg: 'fail to decrypt',
                        type: ErrorType.MPNotSupportCipher,
                        sender: this
                    });
                }
                return decryptedData;
            }
            else {
                /* istanbul ignore next */
                return this.resolve(ServiceIdentity.NetworkManager).sendByHttpTo(targetUrlRaw, 'GET');
            }
        });
    }
    /** @internal */
    install() {
        this.instance.refreshFileUrl = this.extendFunc(this.refreshFileUrl);
        this.instance.createFileMessage = this.extendFunc(this.createFileMessage);
        this.instance.decryptMedia = this.extendFunc(this.decryptMedia);
        this.instance.parseContent = this.extendFunc(this.parseContent);
        this.instance.getImageResizeUrls = this.extendFunc(this.getImageResizeUrls);
        this.api = this.register(ServiceIdentity.MultimediaApi, MultimediaApiManager);
        this.register(ServiceIdentity.MultimediaPlugin, this);
        this.refreshCache = new TtlCache(50 * 60 * 1000); // ttl 50mins
        this.sendQueue = new PQueue({ concurrency: this.ctx.option.serialSendFileMessage ? 1 : Infinity });
    }
    // #region Private Methods
    __internal_sendFileMessage(msg) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            msg.flightStatus = FlightStatus.Preparing;
            let fileMsg;
            try {
                const uploadPromise = this.__internal_getFileUrlWithUpload(msg, msg.fileInfo);
                // 启动上传不参与排队，只有结束需要排队
                fileMsg = yield this.sendQueue.add(() => uploadPromise);
            }
            catch (err) /* istanbul ignore next */ {
                msg.flightStatus = FlightStatus.Failed;
                this.resolve(ServiceIdentity.MessageManager).upsert(msg);
                return {
                    success: false,
                    payload: msg,
                    innerError: err,
                    statusCode: (_a = err.type) !== null && _a !== void 0 ? _a : ErrorType.MPUploadError
                };
            }
            /* istanbul ignore next */
            if (!fileMsg) {
                msg.flightStatus = FlightStatus.Failed;
                this.resolve(ServiceIdentity.MessageManager).upsert(msg);
                return {
                    success: false,
                    payload: msg,
                    statusMsg: 'unknown error when send file',
                    statusCode: ErrorType.MPUploadError
                };
            }
            const ext = {
                [fileMsg.displayType]: fileMsg
            };
            msg.content = JSON.stringify({ __files: ext }); // 和其他两端保持对齐;
            // write cache after refreshing
            this.refreshCache.set(msg.clientId, true);
            return this.sendQueue.add(() => this.instance.__internal_sendMessageObject.apply(this.instance, [msg]));
        });
    }
    /**
     * 获取图片URL（缩放、转码）
     * @param params.uri 图片uri
     * @param params.format 图片格式，可选，目前支持 jpeg, png, webp, gif
     * @param params.width 宽
     * @param params.height 长
     */
    getImageResizeUrls(params) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.api.GetMediaUrls({
                sourceAppId: this.ctx.option.appId,
                mediaType: im_proto.MediaType.IMG,
                uri: params.uri,
                // https://code.remove.this/im_cloud/im_go_api/blob/c7f594bae4155e2b4973af69d9252b4542acd7b6/core/handlers/media.go#L429-429
                // 后端 options 的 tplv 是按顺序写死的……所以 resize 参数需要排第二个才生效
                imgOptions: [
                    {
                        tplv: ImageTplvType.Obj,
                        format: (_a = params.format) !== null && _a !== void 0 ? _a : ImageTplvFormat
                    },
                    {
                        tplv: ImageTplvType.Resize,
                        format: (_b = params.format) !== null && _b !== void 0 ? _b : ImageTplvFormat,
                        params: [String(params.width), String(params.height)]
                    }
                ]
            });
            const urls = (_f = (_e = (_d = (_c = resp.body) === null || _c === void 0 ? void 0 : _c.get_media_urls_body) === null || _d === void 0 ? void 0 : _d.img_options) === null || _e === void 0 ? void 0 : _e[1]) === null || _f === void 0 ? void 0 : _f.urls; // 取第二个 imgOptions 对应的结果
            if (!Array.isArray(urls)) {
                /* istanbul ignore next */
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'respsone image url from server error',
                    type: ErrorType.MPServerUrlError,
                    sender: this,
                    logid: resp.log_id
                });
            }
            return urls;
        });
    }
    __internal_getFileUrl(fileMsg) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return __awaiter(this, void 0, void 0, function* () {
            const type = fileMsg.ext[FileExtKey.Type];
            if (!type) {
                /* istanbul ignore next */
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'invalid request of get media url',
                    type: ErrorType.MPInvalidArgument,
                    sender: this
                });
            }
            const needEncrypt = fileMsg.ext[FileExtKey.NeedEncrypt] === '1';
            const reqBody = {
                sourceAppId: this.ctx.option.appId,
                uri: fileMsg.ext[FileExtKey.Uri],
                mediaType: im_proto.MediaType.OTHER,
                encrypted: needEncrypt,
                imgOptions: [{}],
                videoOptions: [{}],
                audioOptions: [{}],
                fileOptions: [{}],
                encryptedUri: needEncrypt ? fileMsg.ext[FileExtKey.EncryptUri] : undefined
            };
            switch (type) {
                case FileExtTypeValue.Image:
                    reqBody.mediaType = im_proto.MediaType.IMG;
                    reqBody.imgOptions = [
                        {
                            tplv: ImageTplvType.Obj,
                            format: (_a = fileMsg.ext[FileExtKey.ImgSuffix]) !== null && _a !== void 0 ? _a : ImageTplvFormat
                        }
                    ];
                    // check key exist, due to some old clients may not contain these key
                    if (fileMsg.ext[FileExtKey.ImgPreviewWidth] !== undefined &&
                        fileMsg.ext[FileExtKey.ImgPreviewHeight] !== undefined) {
                        reqBody.imgOptions.push({
                            tplv: ImageTplvType.Resize,
                            format: (_b = fileMsg.ext[FileExtKey.ImgSuffix]) !== null && _b !== void 0 ? _b : ImageTplvFormat,
                            params: [fileMsg.ext[FileExtKey.ImgPreviewWidth], fileMsg.ext[FileExtKey.ImgPreviewHeight]] // width:height
                        });
                    }
                    if (fileMsg.ext[FileExtKey.ImgThumbWidth] !== undefined &&
                        fileMsg.ext[FileExtKey.ImgThumbHeight] !== undefined) {
                        reqBody.imgOptions.push({
                            tplv: ImageTplvType.Resize,
                            format: (_c = fileMsg.ext[FileExtKey.ImgSuffix]) !== null && _c !== void 0 ? _c : ImageTplvFormat,
                            params: [fileMsg.ext[FileExtKey.ImgThumbWidth], fileMsg.ext[FileExtKey.ImgThumbHeight]]
                        });
                    }
                    break;
                case FileExtTypeValue.Video:
                    reqBody.mediaType = im_proto.MediaType.VIDEO;
                    reqBody.videoOptions = [
                        {
                            vid: fileMsg.ext[FileExtKey.Vid],
                            coverUri: fileMsg.ext[FileExtKey.VideoCoverUri]
                        }
                    ];
                    break;
                case FileExtTypeValue.Audio:
                    reqBody.mediaType = im_proto.MediaType.AUDIO;
                    reqBody.audioOptions = [
                        {
                            vid: fileMsg.ext[FileExtKey.Vid]
                        }
                    ];
                    break;
                default:
                    reqBody.mediaType = im_proto.MediaType.FILE;
                    reqBody.fileOptions = [
                        {
                            urls: []
                        }
                    ];
                    break;
            }
            const resp = yield this.api.GetMediaUrls(reqBody);
            const respBody = resp.body.get_media_urls_body;
            switch (type) {
                case FileExtTypeValue.Image:
                    const imgOpt = respBody.img_options;
                    if (((_d = respBody.encrypted_urls) === null || _d === void 0 ? void 0 : _d.length) === 0 && (!imgOpt || imgOpt.length < 3)) {
                        /* istanbul ignore next */
                        throw new BaseError({
                            ctx: this.ctx,
                            msg: 'respsone image url from server error',
                            type: ErrorType.MPServerUrlError,
                            sender: this,
                            logid: resp.log_id
                        });
                    }
                    if (needEncrypt && ((_e = respBody.encrypted_urls) === null || _e === void 0 ? void 0 : _e.length) !== 0) {
                        fileMsg.encryptUrl = respBody.encrypted_urls[0];
                        fileMsg.ext[FileExtKey.ImgPreviewEncryptUrl] = respBody.encrypted_urls[1];
                        fileMsg.ext[FileExtKey.ImgThumbEncryptUrl] = respBody.encrypted_urls[2];
                    }
                    if (imgOpt.length !== 0) {
                        fileMsg.remoteURL = imgOpt[0].urls[0]; // 图片链接
                        fileMsg.ext[FileExtKey.ImgPreviewUrl] = (_f = imgOpt[1]) === null || _f === void 0 ? void 0 : _f.urls[0]; // 缩略图链接
                        fileMsg.ext[FileExtKey.ImgThumbUrl] = (_g = imgOpt[2]) === null || _g === void 0 ? void 0 : _g.urls[0]; // 缩略图链接
                    }
                    break;
                case FileExtTypeValue.Video:
                    const vidOpt = respBody.video_options;
                    if (!vidOpt || vidOpt.length < 1) {
                        /* istanbul ignore next */
                        throw new BaseError({
                            ctx: this.ctx,
                            msg: 'respsone video url from server error',
                            type: ErrorType.MPServerUrlError,
                            sender: this,
                            logid: resp.log_id
                        });
                    }
                    fileMsg.remoteURL = vidOpt[0].urls[0]; // 视频链接
                    // 后端接口会忽略传入的 cover_uri，故需要单独获取图片 URL
                    const respForVideoCover = yield this.api.GetMediaUrls({
                        sourceAppId: this.ctx.option.appId,
                        uri: fileMsg.ext[FileExtKey.VideoCoverUri],
                        mediaType: im_proto.MediaType.IMG,
                        imgOptions: [
                            {
                                tplv: ImageTplvType.Obj,
                                format: (_h = fileMsg.ext[FileExtKey.ImgSuffix]) !== null && _h !== void 0 ? _h : ImageTplvFormat
                            }
                        ]
                    });
                    const respBodyForVideoCover = respForVideoCover.body.get_media_urls_body;
                    const coverImgOpt = respBodyForVideoCover.img_options;
                    if (!coverImgOpt || coverImgOpt.length < 1) {
                        /* istanbul ignore next */
                        throw new BaseError({
                            ctx: this.ctx,
                            msg: 'respsone video cover url from server error',
                            type: ErrorType.MPServerUrlError,
                            sender: this,
                            logid: resp.log_id
                        });
                    }
                    fileMsg.ext[FileExtKey.VideoCoverUrl] = coverImgOpt[0].urls[0]; // 封面链接
                    break;
                case FileExtTypeValue.Audio:
                    const audOpt = respBody.audio_options;
                    if (!audOpt || audOpt.length < 1) {
                        /* istanbul ignore next */
                        throw new BaseError({
                            ctx: this.ctx,
                            msg: 'respsone audio url from server error',
                            type: ErrorType.MPServerUrlError,
                            sender: this,
                            logid: resp.log_id
                        });
                    }
                    fileMsg.remoteURL = audOpt[0].urls[0]; // 音频链接
                    break;
                default:
                    const fileOpt = respBody.file_options;
                    if (!fileOpt || fileOpt.length < 1) {
                        /* istanbul ignore next */
                        throw new BaseError({
                            ctx: this.ctx,
                            msg: 'respsone file url from server error',
                            type: ErrorType.MPServerUrlError,
                            sender: this,
                            logid: resp.log_id
                        });
                    }
                    fileMsg.remoteURL = fileOpt[0].urls[0]; // 文件链接
            }
            return {
                image: respBody.img_options,
                video: respBody.video_options,
                audio: respBody.audio_options,
                file: respBody.file_options
            };
        });
    }
    __internal_getToken(tokenType) {
        return __awaiter(this, void 0, void 0, function* () {
            const { ctx } = this;
            if (!this.__internal_memoizedTokenFunc) {
                this.__internal_memoizedTokenFunc = moize(this.api.GetMediaUploadToken.bind(this.api), {
                    isPromise: true,
                    maxSize: 2,
                    maxAge: 1000 * 60 * 12,
                    onCacheAdd() {
                        Logger.ctxDebug(ctx, `cache media upload token with type: ${im_proto.MediaTokenType[tokenType]}`);
                    },
                    onCacheHit() {
                        Logger.ctxDebug(ctx, `media upload token cache with type: ${im_proto.MediaTokenType[tokenType]} hit`);
                    },
                    onExpire(key) {
                        // TODO: 测试缓存过期
                        /* istanbul ignore next */
                        Logger.ctxDebug(ctx, `media upload token with type: ${im_proto.MediaTokenType[key]} expired`);
                    }
                });
            }
            return this.__internal_memoizedTokenFunc(tokenType);
        });
    }
    __internal_scaleImage(source, target) {
        var _a, _b;
        if (target[0] === undefined && target[1] === undefined) {
            /* istanbul ignore next */
            return source;
        }
        const scaleRatio = Math.min(Number((_a = target[0]) !== null && _a !== void 0 ? _a : Number.MAX_SAFE_INTEGER) / source[0], Number((_b = target[1]) !== null && _b !== void 0 ? _b : Number.MAX_SAFE_INTEGER) / source[1]);
        if (scaleRatio > 1.0) {
            // do not larger than source
            return source;
        }
        return [source[0] * scaleRatio, source[1] * scaleRatio];
    }
    __internal_getFileUrlWithUpload(msg, fileInfo) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2;
        return __awaiter(this, void 0, void 0, function* () {
            const { encryptOption, infor } = yield this.attemptUploadFile(fileInfo, msg);
            // 处理视频封面逻辑
            let videoCoverUri;
            if (fileInfo.type === FileType.Video && fileInfo.videoCoverHandler) {
                const { infor: videoCoverInfor } = yield this.attemptUploadFile({
                    fileHandler: fileInfo.videoCoverHandler,
                    type: FileType.Image,
                    displayType: 'media'
                }, msg);
                videoCoverUri = videoCoverInfor === null || videoCoverInfor === void 0 ? void 0 : videoCoverInfor.oid;
            }
            if (infor.type !== 'success') {
                /* istanbul ignore next */
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'uplaod file to video-cloud error',
                    type: ErrorType.MPUploadError,
                    sender: this
                });
            }
            // ******** get file url *****
            const fileMsg = {
                displayType: fileInfo.displayType,
                mime: fileInfo.mime,
                length: fileInfo.length,
                fileName: fileInfo.fileName,
                type: (_a = fileInfo.fileName) === null || _a === void 0 ? void 0 : _a.slice(fileInfo.fileName.lastIndexOf('.') + 1),
                ext: Object.assign({ [FileExtKey.AppId]: this.ctx.option.appId.toString(), [FileExtKey.NeedEncrypt]: '0', [FileExtKey.FileName]: fileInfo.fileName }, fileInfo.ext)
            };
            Logger.ctxDebug(this.ctx, 'upload:', infor);
            if (encryptOption !== undefined) {
                fileMsg.ext[FileExtKey.NeedEncrypt] = '1';
                fileMsg.algorithm = (_c = (_b = infor === null || infor === void 0 ? void 0 : infor.uploadResult) === null || _b === void 0 ? void 0 : _b.Encryption) === null || _c === void 0 ? void 0 : _c.Algorithm;
                fileMsg.secretKey = (_e = (_d = infor === null || infor === void 0 ? void 0 : infor.uploadResult) === null || _d === void 0 ? void 0 : _d.Encryption) === null || _e === void 0 ? void 0 : _e.SecretKey;
                fileMsg.md5 = (_g = (_f = infor === null || infor === void 0 ? void 0 : infor.uploadResult) === null || _f === void 0 ? void 0 : _f.Encryption) === null || _g === void 0 ? void 0 : _g.SourceMd5;
                fileMsg.ext[FileExtKey.EncryptUri] = (_j = (_h = infor === null || infor === void 0 ? void 0 : infor.uploadResult) === null || _h === void 0 ? void 0 : _h.Encryption) === null || _j === void 0 ? void 0 : _j.Uri;
            }
            // 是cipher或者true时, 不处理源文件
            if (fileInfo.encrypt === undefined || fileInfo.encrypt === 'both') {
                Logger.ctxDebug(this.ctx, 'save encrypted file only:', fileInfo.encrypt, ', keep original file');
                fileMsg.remoteURL = '';
                fileMsg.ext[FileExtKey.Uri] = infor.oid;
            }
            // 通过format计算mime
            // FIXME: 上层传入的 params.fileInfo.mime = params.fileInfo.mime ?? params.fileInfo.fileHandler?.type; File.type 默认为空字符串，下面这个分支可能完全不会触发
            /* istanbul ignore next */
            if (fileInfo.mime === undefined && fileInfo.type === FileType.Image) {
                fileInfo.mime = ((_l = (_k = infor.image) === null || _k === void 0 ? void 0 : _k.meta) === null || _l === void 0 ? void 0 : _l.format) ? `image/${infor.image.meta.format}` : undefined;
                // 似乎重复赋值了，上面那句没用
                fileInfo.mime = infor.uploadResult.ImageFormat ?
                    `image/${infor.uploadResult.ImageFormat}` :
                    undefined;
                fileMsg.mime = fileInfo.mime;
                Logger.ctxDebug(this.ctx, 'calculate mime:', fileInfo.mime);
            }
            // calculate thumb scale size
            // (infor?.uploadResult as IImageUploadResult).ImageHeight!.toString() ?? '';
            // const imageUploadResult:IImageUploadResult = infor?.uploadResult;
            if (fileInfo.type === FileType.Image &&
                ((_m = infor === null || infor === void 0 ? void 0 : infor.uploadResult) === null || _m === void 0 ? void 0 : _m.ImageHeight) &&
                ((_o = infor === null || infor === void 0 ? void 0 : infor.uploadResult) === null || _o === void 0 ? void 0 : _o.ImageWidth)) {
                const height = Number.parseInt((_q = (_p = infor === null || infor === void 0 ? void 0 : infor.uploadResult) === null || _p === void 0 ? void 0 : _p.ImageHeight.toString()) !== null && _q !== void 0 ? _q : '', 10);
                const width = Number.parseInt((_s = (_r = infor === null || infor === void 0 ? void 0 : infor.uploadResult) === null || _r === void 0 ? void 0 : _r.ImageWidth.toString()) !== null && _s !== void 0 ? _s : '', 10);
                if (fileInfo.imageThumbHeight === undefined || fileInfo.imageThumbWidth === undefined) {
                    if (fileInfo.imageThumbHeight === undefined && fileInfo.imageThumbWidth === undefined) {
                        fileInfo.imageThumbHeight = 100;
                        fileInfo.imageThumbWidth = 100;
                    }
                    const [newThumbWidth, newThumbHeight] = this.__internal_scaleImage([width, height], [fileInfo.imageThumbWidth, fileInfo.imageThumbHeight]);
                    fileInfo.imageThumbHeight = Math.round(newThumbHeight);
                    fileInfo.imageThumbWidth = Math.round(newThumbWidth);
                }
                if (fileInfo.imagePreviewHeight === undefined || fileInfo.imagePreviewWidth === undefined) {
                    if (fileInfo.imagePreviewHeight === undefined && fileInfo.imagePreviewWidth === undefined) {
                        fileInfo.imagePreviewHeight = 500;
                        fileInfo.imagePreviewWidth = 500;
                    }
                    const [newPreviewWidth, newPreviewHeight] = this.__internal_scaleImage([width, height], [fileInfo.imagePreviewWidth, fileInfo.imagePreviewHeight]);
                    fileInfo.imagePreviewWidth = Math.round(newPreviewWidth);
                    fileInfo.imagePreviewHeight = Math.round(newPreviewHeight);
                }
            }
            switch (fileInfo.type) {
                case FileType.Image:
                    fileMsg.ext[FileExtKey.Type] = FileExtTypeValue.Image; // 文件类型
                    fileMsg.ext[FileExtKey.ImgHeight] =
                        (_t = (infor === null || infor === void 0 ? void 0 : infor.uploadResult).ImageHeight.toString()) !== null && _t !== void 0 ? _t : '';
                    fileMsg.ext[FileExtKey.ImgWidth] =
                        (_u = (infor === null || infor === void 0 ? void 0 : infor.uploadResult).ImageWidth.toString()) !== null && _u !== void 0 ? _u : '';
                    fileMsg.ext[FileExtKey.ImgThumbHeight] = (_v = fileInfo.imageThumbHeight) === null || _v === void 0 ? void 0 : _v.toString(); // 缩略图，长
                    fileMsg.ext[FileExtKey.ImgThumbWidth] = (_w = fileInfo.imageThumbWidth) === null || _w === void 0 ? void 0 : _w.toString(); // 缩略图，宽
                    fileMsg.ext[FileExtKey.ImgPreviewWidth] = (_x = fileInfo.imagePreviewWidth) === null || _x === void 0 ? void 0 : _x.toString(); // 预览图，长
                    fileMsg.ext[FileExtKey.ImgPreviewHeight] = (_y = fileInfo.imagePreviewHeight) === null || _y === void 0 ? void 0 : _y.toString(); // 预览图，宽
                    fileMsg.ext[FileExtKey.ImgUseImageX] = '1';
                    fileMsg.ext[FileExtKey.ImgSuffix] = fileInfo.imageSuffix;
                    break;
                case FileType.Video:
                    fileMsg.ext[FileExtKey.Type] = FileExtTypeValue.Video; // 文件类型
                    fileMsg.ext[FileExtKey.Vid] = infor.uploadResult.Vid;
                    fileMsg.ext[FileExtKey.VideoCoverUri] =
                        videoCoverUri !== null && videoCoverUri !== void 0 ? videoCoverUri : (infor === null || infor === void 0 ? void 0 : infor.uploadResult).PosterUri;
                    fileMsg.ext[FileExtKey.VideoDuration] = (infor === null || infor === void 0 ? void 0 : infor.uploadResult).VideoMeta.Duration.toString();
                    fileMsg.ext[FileExtKey.VideoHeight] = (infor === null || infor === void 0 ? void 0 : infor.uploadResult).VideoMeta.Height.toString();
                    fileMsg.ext[FileExtKey.VideoWidth] = (infor === null || infor === void 0 ? void 0 : infor.uploadResult).VideoMeta.Width.toString();
                    break;
                case FileType.Audio:
                    fileMsg.ext[FileExtKey.Type] = FileExtTypeValue.Audio; // 文件类型
                    fileMsg.ext[FileExtKey.Vid] = infor.uploadResult.Vid;
                    fileMsg.ext[FileExtKey.AudioDuration] =
                        ((_z = fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.audioDuration) === null || _z === void 0 ? void 0 : _z.toString()) ||
                            ((_2 = (_1 = (_0 = infor.uploadResult) === null || _0 === void 0 ? void 0 : _0.VideoMeta) === null || _1 === void 0 ? void 0 : _1.Duration) === null || _2 === void 0 ? void 0 : _2.toString());
                    break;
                default: // 文件类型
                    fileMsg.ext[FileExtKey.Type] = FileExtTypeValue.File;
                    break;
            }
            const ext = {
                [fileMsg.displayType]: fileMsg
            };
            msg.content = JSON.stringify({ __files: ext });
            this.resolve(ServiceIdentity.MessageManager).upsert(msg);
            yield this.__internal_getFileUrl(fileMsg);
            return fileMsg;
        });
    }
    attemptUploadFile(fileInfo, msg) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = Monitor.performanceNow();
            // 图片使用imagex, 其他还是走vsdk
            const tokenType = fileInfo.type === FileType.Image ? im_proto.MediaTokenType.IMAGEX_V5 : im_proto.MediaTokenType.VSDK_V5;
            const resp = yield this.__internal_getToken(tokenType);
            const stsToken = {
                CurrentTime: resp.current_time,
                ExpiredTime: resp.expire_time,
                SessionToken: resp.token,
                AccessKeyId: resp.access_key,
                SecretAccessKey: resp === null || resp === void 0 ? void 0 : resp.secret_access_key
            };
            const videoConfig = {
                spaceName: resp.space_name,
                processAction: [
                    {
                        name: 'Snapshot',
                        input: {
                            SnapshotTime: 0,
                            SkipBlackDetect: true
                        }
                    }
                ]
            };
            const imageConfig = {
                serviceId: resp.service_id,
                processAction: []
            };
            let encryptOption;
            if (fileInfo.encrypt) {
                let copies = fileInfo.encrypt === true ? 'cipher_v2' : 'both';
                if (fileInfo.encrypt === 'both' || fileInfo.encrypt === 'cipher_v2') {
                    copies = fileInfo.encrypt;
                }
                encryptOption = {
                    name: 'Encryption',
                    input: {
                        Config: {
                            copies
                        }
                    }
                };
            }
            if (encryptOption) {
                imageConfig.processAction.push(encryptOption);
            }
            const region = (_b = (_a = this.ctx.option) === null || _a === void 0 ? void 0 : _a.uploadRegion) !== null && _b !== void 0 ? _b : MultimediaPluginConsts.uploadRegion;
            const videoHost = (_c = this.ctx.option) === null || _c === void 0 ? void 0 : _c.videoDomain;
            const imageHost = (_d = this.ctx.option) === null || _d === void 0 ? void 0 : _d.imageDomain;
            // ******** upload file *****
            const uploader = new (AdapterManager.getConstructor(this.ctx, AdapterType.MediaUploader))(this.ctx, {
                appId: (_e = this.ctx.option) === null || _e === void 0 ? void 0 : _e.appId,
                userId: (_f = this.ctx.option) === null || _f === void 0 ? void 0 : _f.userId,
                stsToken,
                videoConfig,
                imageConfig,
                region,
                videoHost,
                imageHost,
                onUploadProcess: fileInfo.onUploadProcess
            });
            uploader.performAddFile(fileInfo);
            let infor;
            try {
                infor = yield uploader.performUpload();
                const endTime = Monitor.performanceNow();
                this.resolve(ServiceIdentity.Monitor).emitTracker(TrackerItem.MultimediaMessageUpload, {
                    conversation_id: msg.conversationId,
                    result: '1',
                    msg_type: msg.type.toString(),
                    upload_cost_time: endTime - startTime,
                    error_msg: '',
                    error_code: '0'
                });
            }
            catch (err) /* istanbul ignore next */ {
                const endTime = Monitor.performanceNow();
                this.resolve(ServiceIdentity.Monitor).emitTracker(TrackerItem.MultimediaMessageUpload, {
                    conversation_id: msg.conversationId,
                    result: '0',
                    msg_type: msg.type.toString(),
                    upload_cost_time: endTime - startTime,
                    error_msg: err,
                    error_code: ''
                });
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'uplaod file to video-cloud error',
                    type: ErrorType.MPUploadError,
                    sender: this,
                    innerError: err
                });
            }
            return { encryptOption, infor };
        });
    }
    initAdapter() {
        // impl by sub class
    }
}

export { MultimediaPluginCore };
//# sourceMappingURL=MultimediaPlugin.js.map
